import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@stores/auth.store';
import { isTabBackgrounded } from '@utils/sessionNetworkCoordinator';
import { getNotifications, getNotificationsUnreadCount } from '@services/notifications.service';
import {
    NOTIFICATIONS_QUERY_KEY,
    NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
} from '@hooks/useNotifications';

/**
 * Sidebar badge only — reads list total from cache (populated by NotificationsStreamManager) to avoid duplicate list fetches.
 */
export function useNotificationBadge() {
    const accessToken = useAuthStore((state) => state.accessToken);
    const hasToken = !!accessToken;
    const queriesEnabled = hasToken && !isTabBackgrounded();

    const unreadCountQuery = useQuery({
        queryKey: NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
        queryFn: getNotificationsUnreadCount,
        enabled: queriesEnabled,
        retry: (failureCount, error) => {
            if (error?.response?.status === 401) return false;
            return failureCount < 1;
        },
    });

    const listMetaQuery = useQuery({
        queryKey: [...NOTIFICATIONS_QUERY_KEY, { page: 1, limit: 50 }],
        queryFn: () => getNotifications({ page: 1, limit: 50 }),
        enabled: false,
        staleTime: Infinity,
    });

    const unreadCount =
        !unreadCountQuery.isError &&
        typeof unreadCountQuery.data === 'number' &&
        unreadCountQuery.data >= 0
            ? unreadCountQuery.data
            : 0;

    const total = listMetaQuery.data?.total ?? 0;

    return {
        unreadCount,
        total,
        isLoading: unreadCountQuery.isLoading,
    };
}
